<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { ref } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import {
  COMPACT_BUTTON_CLASS as compactButtonClass,
  INPUT_CLASS as inputClass,
  PRIMARY_COMPACT_BUTTON_CLASS as primaryCompactButtonClass,
} from '../utils/pendentesConstants';

defineProps({
  entries: { type: Array, default: () => [] },
  isLoadingEntries: { type: Boolean, default: false },
  isCreating: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  removingEntryId: { type: String, default: '' },
  canCreate: { type: Boolean, default: false },
});

const emit = defineEmits(['search', 'create', 'remove', 'close']);

const search = defineModel('search', { type: String, default: '' });
const phone = defineModel('phone', { type: String, default: '' });
const reason = defineModel('reason', { type: String, default: '' });

const dialogRef = ref(null);

defineExpose({
  open: () => dialogRef.value?.open(),
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="Blacklist"
    cancel-button-label="Fechar"
    :show-confirm-button="false"
    :is-loading="isSaving"
    width="2xl"
    position="top"
    overflow-y-auto
    @close="emit('close')"
  >
    <div class="space-y-4 text-[13px] text-lais-text">
      <div
        class="grid gap-2 rounded-lais border border-lais-border bg-white p-3 sm:grid-cols-[1fr_auto]"
      >
        <label class="relative min-w-0">
          <span
            class="i-lucide-search pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-lais-text-muted"
            aria-hidden="true"
          />
          <input
            v-model="search"
            :class="inputClass"
            class="pl-10"
            placeholder="Buscar telefone, motivo ou origem"
            @keyup.enter="emit('search')"
          />
        </label>
        <button
          type="button"
          :class="compactButtonClass"
          :disabled="isLoadingEntries"
          @click="emit('search')"
        >
          <span
            class="size-4"
            :class="
              isLoadingEntries
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-search'
            "
            aria-hidden="true"
          />
          Buscar
        </button>
      </div>

      <div
        class="grid gap-2 rounded-lais border border-lais-border bg-lais-bg-page p-3 sm:grid-cols-[11rem_1fr_auto]"
      >
        <input v-model="phone" :class="inputClass" placeholder="Telefone" />
        <input
          v-model="reason"
          :class="inputClass"
          placeholder="Motivo"
          @keyup.enter="emit('create')"
        />
        <button
          type="button"
          :class="primaryCompactButtonClass"
          :disabled="!canCreate"
          @click="emit('create')"
        >
          <span
            class="size-4"
            :class="
              isCreating
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-plus'
            "
            aria-hidden="true"
          />
          Adicionar
        </button>
      </div>

      <div
        class="max-h-[48vh] overflow-y-auto rounded-lais border border-lais-border bg-white"
      >
        <div
          v-if="isLoadingEntries"
          class="flex items-center justify-center gap-2 px-4 py-10 text-lais-text-muted"
        >
          <span
            class="i-lucide-loader-circle size-5 animate-spin text-lais-primary"
            aria-hidden="true"
          />
          Carregando blacklist...
        </div>
        <div
          v-else-if="!entries.length"
          class="px-4 py-10 text-center text-lais-text-muted"
        >
          Nenhum telefone ativo na blacklist.
        </div>
        <div v-else class="divide-y divide-lais-border-soft">
          <div
            v-for="entry in entries"
            :key="entry.id"
            class="grid gap-3 px-4 py-3 sm:grid-cols-[11rem_1fr_auto] sm:items-center"
          >
            <div class="min-w-0">
              <p class="truncate font-semibold text-lais-text">
                {{ entry.phone }}
              </p>
              <p
                v-if="entry.ownerName"
                class="mt-0.5 truncate text-[12px] text-lais-text-muted"
              >
                {{ entry.ownerName }}
              </p>
            </div>
            <div class="min-w-0">
              <p class="line-clamp-2 text-[12px] text-lais-text">
                {{ entry.reason }}
              </p>
              <p class="mt-1 truncate text-[11px] text-lais-text-muted">
                {{ entry.sourceLabel || entry.source || 'Origem manual' }}
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-8 items-center justify-center gap-1 rounded-lais border border-lais-border bg-white px-2.5 text-[12px] font-semibold text-lais-text-muted transition hover:border-red-400 hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="Boolean(removingEntryId)"
              @click="emit('remove', entry)"
            >
              <span
                class="size-3.5"
                :class="
                  removingEntryId === String(entry.id)
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-trash-2'
                "
                aria-hidden="true"
              />
              Remover
            </button>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
